import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Stack, Group, Title, Text, Badge, Button } from '@repletoryreach/sdk-ui';
import { Timeline, Alert } from '@repletoryreach/sdk-ui/data-display';
import { useAuditFeed, type AuditSource } from '../../data/useAuditFeed';
import { useTemplateEvent } from '../../data/useTemplateEvent';
import { TenantName } from '../../components/TenantName';
import { formatRelative } from '../../lib/format';
import { useT } from '../../i18n/I18nProvider';

const MAX_ROWS = 8;
const WINDOW_HOURS = 24;

/**
 * The audit fan-in, not the ledger: the ledger query on this page already
 * fetches a thousand rows for the chart, and repeating it here would double
 * that for eight lines of text.
 */
const SOURCES: Record<AuditSource, boolean> = {
  transfers: true,
  'opt-in': true,
  deliveries: true,
  'flow-runs': true,
  ledger: false,
  // An operator inside a customer account is the one thing on this timeline
  // that a person should see the moment they open the dashboard.
  impersonations: true
};

const SOURCE_LABEL_KEY: Record<AuditSource, string> = {
  transfers: 'audit.kpi.transfers',
  'opt-in': 'audit.kpi.optIn',
  deliveries: 'audit.kpi.deliveries',
  'flow-runs': 'audit.kpi.flowRuns',
  ledger: 'audit.kpi.ledger',
  impersonations: 'audit.kpi.impersonations'
};

interface LiveItem {
  id: string;
  ts: string;
  labelKey: string;
  organisationId: string;
  detail: string;
}

/**
 * What has actually happened across the platform, in the last day.
 *
 * This card used to subscribe to template-lifecycle events on the inbox hub.
 * That hub fans out to `tenant:{orgId}` groups and a platform admin joins the
 * platform organisation's group, so the card could never receive an event: it
 * sat permanently on "Submit a template from the tenant console to see one
 * here", describing an action that would not change it. It now reads the same
 * cross-tenant audit fan-in the audit page uses, and still shows a live
 * template event on top if the hub scoping is ever fixed.
 */
export function ActivityTimeline() {
  const t = useT();
  const [live, setLive] = useState<LiveItem[]>([]);

  // One value for the life of the card: a `since` that moved every render would
  // change the query key on every render and refetch forever.
  const since = useMemo(
    () => new Date(Date.now() - WINDOW_HOURS * 3600_000).toISOString(),
    []
  );
  const { rows, loading, failedSources, refetch } = useAuditFeed(since, SOURCES, MAX_ROWS);

  const push = useCallback((item: Omit<LiveItem, 'ts'>) => {
    setLive((prev) => [{ ...item, ts: new Date().toISOString() }, ...prev].slice(0, MAX_ROWS));
  }, []);

  useTemplateEvent('template.submitted', (p) => push({
    id: `${p.template_id}-sub`, labelKey: 'activity.kind.submitted', organisationId: p.organisation_id, detail: p.code
  }));
  useTemplateEvent('template.approved', (p) => push({
    id: `${p.template_id}-app`, labelKey: 'activity.kind.approved', organisationId: p.organisation_id, detail: p.code
  }));
  useTemplateEvent('template.rejected', (p) => push({
    id: `${p.template_id}-rej`, labelKey: 'activity.kind.rejected', organisationId: p.organisation_id, detail: p.code
  }));

  const items = [
    ...live.map((l) => ({
      key: `${l.id}-${l.ts}`,
      labelKey: l.labelKey,
      organisationId: l.organisationId,
      detail: l.detail,
      ts: l.ts,
      isLive: true
    })),
    ...rows.slice(0, MAX_ROWS).map((r) => ({
      key: `${r.source}-${r.id}`,
      labelKey: SOURCE_LABEL_KEY[r.source],
      organisationId: r.organisation_id,
      detail: r.summary,
      ts: r.occurred_at,
      isLive: false
    }))
  ].slice(0, MAX_ROWS);

  return (
    <Card withBorder>
      <Stack gap="md">
        <Group justify="space-between" align="baseline">
          <Title order={4}>{t('activity.title')}</Title>
          <Text fz="xs" c="var(--rr-color-text-muted)">{t('activity.subtitle')}</Text>
        </Group>

        {/* A source that could not be asked is not a quiet platform. */}
        {failedSources.length > 0 && (
          <Alert color="warning">
            <Stack gap="xs" align="flex-start">
              <Text fz="sm">{t('activity.loadError')}</Text>
              <Button size="xs" variant="outline" onClick={refetch}>{t('action.retry')}</Button>
            </Stack>
          </Alert>
        )}

        {loading && items.length === 0 ? (
          <Text fz="sm" c="var(--rr-color-text-muted)">{t('activity.loading')}</Text>
        ) : items.length === 0 ? (
          <Text fz="sm" c="var(--rr-color-text-muted)">{t('activity.empty')}</Text>
        ) : (
          <Timeline active={items.length - 1} bulletSize={18} lineWidth={2}>
            {items.map((item) => (
              <Timeline.Item
                key={item.key}
                title={
                  <Group gap="xs" align="baseline" wrap="nowrap" style={{ minWidth: 0 }}>
                    <Badge size="xs" variant="light">{t(item.labelKey)}</Badge>
                    {item.isLive && <Badge size="xs" variant="outline">{t('activity.live')}</Badge>}
                    <TenantName id={item.organisationId} subtle />
                  </Group>
                }
              >
                <Group justify="space-between" wrap="nowrap" gap="sm">
                  <Text fz="xs" c="var(--rr-color-text-muted)" lineClamp={1}>{item.detail}</Text>
                  <Text fz="xs" c="var(--rr-color-text-muted)" style={{ flexShrink: 0 }}>{formatRelative(item.ts)}</Text>
                </Group>
              </Timeline.Item>
            ))}
          </Timeline>
        )}

        <Link to="/audit" style={{ color: 'var(--rr-color-primary)', fontSize: 13 }}>
          {t('activity.viewAll')}
        </Link>
      </Stack>
    </Card>
  );
}
